import React, { useState, useEffect } from "react";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getLogs } from "../utils/logUtils";
import moment from "moment";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const styles = {
  safeArea: {
    minHeight: "100vh",
    backgroundColor: COLORS.white,
  },
  container: {
    maxWidth: 1350,
    margin: "0 auto",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "row",
    gap: 24,
    backgroundColor: COLORS.white,
    minHeight: "100vh",
    animation: "fadeIn 0.5s ease-in-out",
  },
  calendarContainer: {
    width: 320,
    flexShrink: 0,
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 12,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
  calendar: {
    border: "none",
    borderRadius: 8,
    backgroundColor: COLORS.white,
    fontFamily: FONTS.regularInter,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  title: {
    fontSize: 28,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    marginBottom: 16,
    fontWeight: 700,
  },
  logsContainer: {
    flex: 1,
    overflowY: "auto",
    maxHeight: "calc(100vh - 64px)",
  },
  searchContainer: {
    position: "relative",
    marginBottom: 16,
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px 10px 40px",
    borderRadius: 8,
    border: `1px solid ${COLORS.tertiary}`,
    fontSize: 14,
    fontFamily: FONTS.regularInter,
    backgroundColor: COLORS.white,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s ease",
    outline: "none",
    "&:focus": {
      borderColor: COLORS.primary,
      boxShadow: `0 0 0 2px ${COLORS.primary}40`,
    },
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    width: 18,
    height: 18,
    color: COLORS.text,
    opacity: 0.5,
  },
  logItem: {
    padding: 16,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 8,
    marginBottom: 12,
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: `${COLORS.primary}10`,
      boxShadow: "0 5px 14px rgba(0, 0, 0, 0.12)",
      transform: "translateY(-2px)",
    },
  },
  logGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    alignItems: "start",
  },
  logField: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  logLabel: {
    fontSize: 13,
    fontFamily: FONTS.bold,
    color: COLORS.text,
    width: 70,
    fontWeight: 600,
  },
  logValue: {
    fontSize: 13,
    fontFamily: FONTS.regularInter,
    color: COLORS.text,
    opacity: 0.8,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  loader: {
    fontSize: 16,
    fontFamily: FONTS.regularInter,
    color: COLORS.text,
    textAlign: "center",
    padding: "24px 0",
    animation: "pulse 1.5s infinite",
  },
  emptyContainer: {
    padding: 24,
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.tertiary}`,
    borderRadius: 8,
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.08)",
    textAlign: "center",
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regularInter,
    color: COLORS.text,
    opacity: 0.6,
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.5 },
    "100%": { opacity: 1 },
  },
  "@media (max-width: 1024px)": {
    container: {
      flexDirection: "column",
      padding: "16px 12px",
    },
    calendarContainer: {
      width: "100%",
      maxWidth: "none",
    },
    logsContainer: {
      maxHeight: "none",
    },
    logGrid: {
      gridTemplateColumns: "1fr",
    },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 8px",
    },
    title: {
      fontSize: 24,
    },
    calendarContainer: {
      padding: 8,
    },
    searchInput: {
      padding: "8px 12px 8px 36px",
      fontSize: 13,
    },
    searchIcon: {
      left: 10,
      width: 16,
      height: 16,
    },
    logItem: {
      padding: 12,
    },
    logLabel: {
      fontSize: 12,
      width: 60,
    },
    logValue: {
      fontSize: 12,
    },
    loader: {
      fontSize: 14,
    },
    emptyText: {
      fontSize: 13,
    },
  },
};

const DriverLogHistoryScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const logList = await getLogs(user.uid);
        setLogs(logList);
        setFilteredLogs(logList);
      } catch (error) {
        setLogs([]);
        setFilteredLogs([]);
      } finally {
        setFetching(false);
      }
    };
    fetchLogs();
  }, [user.uid]);

  useEffect(() => {
    const filtered = logs.filter((log) =>
      log.jobName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredLogs(filtered);
  }, [searchQuery, logs]);

  const handleDateSelect = async (date) => {
    const dateString = moment(date).format("YYYY-MM-DD");
    setSelectedDate(dateString);
    setLoading(true);
    try {
      const logList = await getLogs(user.uid, dateString);
      setLogs(logList);
      setFilteredLogs(
        logList.filter((log) =>
          log.jobName.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } catch (error) {
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditLog = (log) => {
    navigate(`/user/log?log=${encodeURIComponent(JSON.stringify(log))}`);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const renderLogItem = (item) => (
    <div
      style={styles.logItem}
      key={item.id}
      onClick={() => handleEditLog(item)}
    >
      <div style={styles.logGrid}>
        <div style={styles.logField}>
          <span style={styles.logLabel}>Contact</span>
          <span style={styles.logValue}>{item.contactName}</span>
        </div>
        <div style={styles.logField}>
          <span style={styles.logLabel}>Job</span>
          <span style={styles.logValue}>{item.jobName}</span>
        </div>
        <div style={styles.logField}>
          <span style={styles.logLabel}>Price</span>
          <span style={styles.logValue}>
            ${item.totalPrice?.toFixed(2) || "N/A"}/{item.unit || "hr"}
          </span>
        </div>
        <div style={styles.logField}>
          <span style={styles.logLabel}>Date</span>
          <span style={styles.logValue}>
            {item.startTime
              ? moment(item.startTime).format("MM/DD/YYYY")
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <style jsx>{`
          .react-calendar {
            border: none !important;
            border-radius: 8px !important;
            font-family: ${FONTS.regularInter} !important;
            background: ${COLORS.white} !important;
            padding: 8px;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          .react-calendar__tile {
            padding: 10px;
            font-size: 13px;
            color: ${COLORS.text};
            border-radius: 6px;
          }
          .react-calendar__tile--active {
            background: ${COLORS.primary} !important;
            color: ${COLORS.white} !important;
          }
          .react-calendar__tile:enabled:hover {
            background: ${COLORS.primary}80 !important;
          }
          .react-calendar__navigation {
            margin-bottom: 8px;
          }
          .react-calendar__navigation button {
            font-size: 14px;
            font-family: ${FONTS.bold} !important;
            color: ${COLORS.text};
            padding: 6px 10px;
            border-radius: 6px;
          }
          .react-calendar__navigation button:enabled:hover {
            background: ${COLORS.primary}20 !important;
          }
          .react-calendar__month-view__weekdays {
            font-size: 13px;
            font-family: ${FONTS.bold} !important;
            color: ${COLORS.text};
          }
        `}</style>
        <div style={styles.calendarContainer}>
          <h1 style={styles.title}>Log History</h1>
          <Calendar
            onChange={handleDateSelect}
            value={selectedDate ? new Date(selectedDate) : null}
            style={styles.calendar}
            tileClassName={({ date }) =>
              selectedDate === moment(date).format("YYYY-MM-DD")
                ? "react-calendar__tile--active"
                : ""
            }
          />
        </div>
        <div style={styles.logsContainer}>
          <div style={styles.searchContainer}>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search by job name..."
              style={styles.searchInput}
            />
            <svg
              style={styles.searchIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {fetching || loading ? (
            <div style={styles.loader}>Loading logs...</div>
          ) : (
            <div>
              {filteredLogs.length === 0 ? (
                <div style={styles.emptyContainer}>
                  <span style={styles.emptyText}>
                    {selectedDate
                      ? `No logs available for ${moment(selectedDate).format(
                          "MM/DD/YYYY"
                        )}`
                      : searchQuery
                      ? "No logs match your search"
                      : "No logs available"}
                  </span>
                </div>
              ) : (
                filteredLogs.map(renderLogItem)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverLogHistoryScreen;
